import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { platformsConfig } from '../platformConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../Platforms/WBT/WBT1/pages/gene.css';
import logoImage from '../PA.svg';

const SEARCH_API_URL = 'https://links.szapfs.org';

function About() {
  const navigate = useNavigate();
  const { platformType } = useParams();
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Find the platform config based on platformType
  const platform = platformsConfig.find(p => p.platformType === platformType);
  const AboutComponent = platform?.aboutComponent;
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // --- Pure DOM search ---
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const destroyDropdown = useCallback(() => {
    if (dropdownRef.current) {
      dropdownRef.current.remove();
      dropdownRef.current = null;
    }
  }, []);

  const createDropdownContainer = useCallback(() => {
    destroyDropdown();
    if (!searchRef.current) return null;
    const rect = searchRef.current.getBoundingClientRect();
    const div = document.createElement('div');
    Object.assign(div.style, {
      position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`,
      width: `${rect.width}px`, background: '#fff', borderRadius: '8px',
      border: '1px solid #e2e8f0',
      maxHeight: '350px', overflowY: 'auto', zIndex: '2147483647',
    });
    document.body.appendChild(div);
    dropdownRef.current = div;
    return div;
  }, [destroyDropdown]);

  const showMessage = useCallback((text) => {
    const div = createDropdownContainer();
    if (!div) return;
    const msg = document.createElement('div');
    Object.assign(msg.style, { padding: '12px 16px', color: '#94a3b8', fontSize: '0.9rem' });
    msg.textContent = text;
    div.appendChild(msg);
  }, [createDropdownContainer]);

  const buildDropdown = useCallback((results) => {
    if (!results.length) { showMessage('No results found'); return; }
    const div = createDropdownContainer();
    if (!div) return;

    results.forEach((result, i) => {
      const row = document.createElement('div');
      Object.assign(row.style, {
        padding: '10px 16px', cursor: 'pointer', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        borderBottom: i < results.length - 1 ? '1px solid #f1f5f9' : 'none',
      });
      row.onmouseenter = () => { row.style.background = '#f8fafc'; };
      row.onmouseleave = () => { row.style.background = '#fff'; };
      row.onclick = () => {
        if (searchInputRef.current) searchInputRef.current.value = '';
        destroyDropdown();
        navigate('/wbt/explore', { state: { searchResult: result } });
      };

      const label = document.createElement('span');
      Object.assign(label.style, { fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' });
      label.textContent = result.label;

      const badge = document.createElement('span');
      Object.assign(badge.style, {
        fontSize: '0.75rem', color: '#94a3b8', background: '#f1f5f9',
        padding: '2px 8px', borderRadius: '4px', textTransform: 'capitalize',
        flexShrink: '0', marginLeft: '12px',
      });
      badge.textContent = result.tab;

      row.appendChild(label);
      row.appendChild(badge);
      div.appendChild(row);
    });
  }, [createDropdownContainer, showMessage, destroyDropdown, navigate]);

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!query.trim()) {
      destroyDropdown();
      return;
    }

    showMessage('Searching...');

    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${SEARCH_API_URL}/search?q=${encodeURIComponent(query.trim())}`);
        if (!res.ok) throw new Error('Search failed');
        const results = await res.json();
        buildDropdown(results);
      } catch (err) {
        console.error('Search error:', err);
        destroyDropdown();
      }
    }, 300);
  }, [buildDropdown, destroyDropdown, showMessage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current && !searchRef.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        destroyDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [destroyDropdown]);

  useEffect(() => {
    return () => destroyDropdown();
  }, [destroyDropdown]);

  if (!platform) {
    return (
      <div className="gene-page">
        <div className="gene-main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <div className="gene-glass-card" style={{ 
            textAlign: 'center', 
            padding: '2rem',
            animation: 'fadeInUp 0.6s ease-out forwards',
            opacity: 0,
            transform: 'translateY(20px)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🚫</div>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#1f2937' }}>Platform Not Found</h2>
            <p style={{ margin: '0', color: '#6b7280' }}>The requested platform could not be located.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleExplore = () => {
    navigate(`/${platformType}/explore`);
  };

  // Animation styles - moved to top for immediate application
  const animationStyles = `
    @keyframes fadeInDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeInLeft {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeInRight {
      from {
        opacity: 0;
        transform: translateX(30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    .animate-nav {
      animation: fadeInDown 0.8s ease-out forwards;
    }

    .animate-sidebar {
      animation: fadeInLeft 0.8s ease-out 0.2s forwards;
    }

    .animate-hero {
      animation: scaleIn 1s ease-out 0.4s forwards;
    }

    .animate-card-1 {
      animation: fadeInUp 0.8s ease-out 0.6s forwards;
    }

    .animate-card-2 {
      animation: fadeInUp 0.8s ease-out 0.8s forwards;
    }

    .animate-feature {
      animation: fadeInRight 0.6s ease-out forwards;
    }

    .animate-stat {
      animation: scaleIn 0.6s ease-out forwards;
    }

    /* Initial hidden states to prevent flash */
    .nav-container {
      opacity: 0;
      transform: translateY(-30px);
    }

    .sidebar-container {
      opacity: 0;
      transform: translateX(-30px);
    }

    .hero-container {
      opacity: 0;
      transform: scale(0.95);
    }

    .content-card {
      opacity: 0;
      transform: translateY(30px);
    }

    .feature-item {
      opacity: 0;
      transform: translateX(30px);
    }

    .stat-item {
      opacity: 0;
      transform: scale(0.95);
    }
  `;

  return (
    <div className="gene-page" style={{ marginTop: '7vh' }}> 
      <style>{animationStyles}</style>
      
      <div 
        className={`navigation-system nav-container ${isLoaded ? 'animate-nav' : ''}`}
      >
        <div className="main-nav-container">
          {/* Logo */}
          <a href="/" className="main-logo">
            <img src={logoImage} alt="PhenomeAtlas" className="main-logo-image" />
          </a>

          {/* Platform Menu */}
          <nav className="platform-menu">
            <a href="/wbt" className="platform-link">WBT-Atlas</a>
            <a href="/contact" className="platform-link">Contact</a>

          </nav>

          {/* Search Bar */}
          <div className="integrated-search" ref={searchRef}>
            <div className="search-input-wrapper">
              <svg className="search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M17.5 17.5L12.5 12.5M14.1667 8.33333C14.1667 11.555 11.555 14.1667 8.33333 14.1667C5.11167 14.1667 2.5 11.555 2.5 8.33333C2.5 5.11167 5.11167 2.5 8.33333 2.5C11.555 2.5 14.1667 5.11167 14.1667 8.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                ref={searchInputRef}
                placeholder="Search genes, diseases, pathways..."
                className="integrated-search-input"
                onChange={handleSearchChange}
              />
              <button className="search-submit-btn-integrated">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6.5 12L11 7.5L6.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* About Content */}
      <div className="gene-main-content1">
        {AboutComponent ? (
          <AboutComponent />
        ) : (
          <div className="gene-glass-card" style={{
            textAlign: 'center',
            padding: '2rem',
            margin: '2rem auto',
            maxWidth: '600px'
          }}>
            <h2 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>About Page Coming Soon</h2>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280' }}>
              {platform.name} about page is currently under development.
            </p>
            {platform.status === 'live' && (
              <button
                onClick={handleExplore}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                Explore Platform
              </button>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

export default About;
